import type { ProductionTerm } from '@/components/vyroba/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useState } from 'react';

/** Dnešek + n dní v pražském čase jako YYYY-MM-DD. */
function denPraha(plusDni: number): string {
  const d = new Date(Date.now() + plusDni * 86_400_000);
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Prague' });
}

/** „úterý 20. 10. 2026" — ať je hned vidět, jaký je to den. */
function fmtDen(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const datum = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1, 12));
  const den = datum.toLocaleDateString('cs-CZ', { weekday: 'long', timeZone: 'UTC' });
  return `${den} ${d}. ${m}. ${y}`;
}

/** „po 20. 10." pro rychlou volbu termínu. */
function fmtKratce(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const datum = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1, 12));
  const den = datum.toLocaleDateString('cs-CZ', { weekday: 'short', timeZone: 'UTC' });
  return `${den} ${d}. ${m}.`;
}

/**
 * Posun neprodaných kusů produktu na zvolený výrobní termín. Výchozí je zítřek
 * (když zítra termín není, nejbližší další). Vybrat jde jen existující
 * neservisní termín — jen k němu je v e-shopu založená dostupnost.
 */
export function PosunoutTerminModal({
  productCode,
  fromTerm,
  terms,
  onConfirm,
  onCancel,
}: {
  productCode: string;
  fromTerm: string;
  terms: ProductionTerm[];
  onConfirm: (toTerm: string) => void;
  onCancel: () => void;
}) {
  useEscapeKey(onCancel);
  const zitra = denPraha(1);
  const platne = terms
    .filter((t) => !t.is_service && t.term_date !== fromTerm)
    .map((t) => t.term_date)
    .sort();
  const platneSet = new Set(platne);
  const nejblizsi = platne.find((t) => t >= zitra) ?? null;
  const [datum, setDatum] = useState(platneSet.has(zitra) ? zitra : (nejblizsi ?? zitra));
  const ok = platneSet.has(datum);
  // Rychlá volba — nejbližší termíny od zítřka.
  const rychle = platne.filter((t) => t >= zitra).slice(0, 6);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onCancel}
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-md bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text mb-1">Posunout termín neprodaných kusů?</h2>
        <p className="text-sm text-text-secondary mb-5">
          Prodané kusy produktu <strong>{productCode}</strong> zůstanou na tomto termínu — na ten už
          čeká zákazník. Neprodané se přesunou na vybraný termín výroby.
        </p>

        <label htmlFor="posun-datum" className="block text-sm font-semibold text-text mb-1.5">
          Nový termín
        </label>
        <input
          id="posun-datum"
          type="date"
          value={datum}
          min={platne[0]}
          max={platne[platne.length - 1]}
          onChange={(e) => setDatum(e.target.value)}
          className={`w-full rounded-lg border px-3 py-2 text-text bg-surface focus:outline-none ${
            ok ? 'border-border focus:border-primary' : 'border-danger'
          }`}
        />
        <p className={`mt-1.5 text-sm ${ok ? 'text-text font-semibold' : 'text-danger'}`}>
          {datum ? fmtDen(datum) : ''}
          {datum && !ok && ' — tento den není výrobní termín'}
        </p>

        {rychle.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {rychle.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setDatum(t)}
                className={`rounded-full border px-3 py-1 text-xs cursor-pointer transition-colors ${
                  t === datum
                    ? 'border-primary bg-primary/10 text-text font-semibold'
                    : 'border-border text-text-secondary hover:bg-surface-hover'
                }`}
              >
                {fmtKratce(t)}
              </button>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-semibold text-text-secondary hover:text-text px-4 py-2 cursor-pointer"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={() => onConfirm(datum)}
            disabled={!ok}
            className="text-sm font-bold rounded-lg px-5 py-2 text-white cursor-pointer transition-colors bg-action hover:bg-action-dark disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Posunout
          </button>
        </div>
      </div>
    </div>
  );
}
